import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

interface PortalPayment {
  id: string; amount: number; currency: string; status: string;
  due_date: string | null; paid_at: string | null; fee_name: string | null; method_name: string | null;
  notes?: string | null; period?: string | null; credit_applied?: number;
}

/** Student portal — outstanding payments + payment history. Online payment
 *  (provider checkout) arrives with the Stripe adapter; for now the school records
 *  external payments and they appear here as paid. */
@customElement('cadence-portal-payments')
export class CadencePortalPayments extends LitElement {
  @state() private loading = true;
  @state() private payments: PortalPayment[] = [];
  @state() private creditBalance = 0;
  @state() private onlinePay = false;
  @state() private payingId: string | null = null;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-4); font-size: 1.5rem; }
    .summary { display: flex; gap: var(--space-4); flex-wrap: wrap; margin-bottom: var(--space-4); }
    .stat { flex: 1; min-width: 160px; padding: var(--space-4); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); }
    .stat .k { color: var(--color-text-secondary); font-size: 0.8rem; }
    .stat .v { font-size: 1.4rem; font-weight: 700; margin-top: 4px; }
    .stat .v.due { color: var(--color-danger); }
    h2 { font-size: 1.05rem; margin: var(--space-4) 0 var(--space-3); }
    .list { display: flex; flex-direction: column; gap: 8px; }
    .item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); }
    .item .name { font-weight: 600; }
    .item .meta { color: var(--color-text-secondary); font-size: 0.82rem; margin-top: 2px; }
    .amount { font-weight: 700; white-space: nowrap; }
    .pay { display: flex; align-items: center; gap: 12px; }
    .muted { color: var(--color-text-secondary); font-size: 0.85rem; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.handleReturn(); this.load(); }

  /** Show feedback when the student returns from Stripe Checkout, then clean the URL.
   *  The webhook does the actual marking, so there may be a brief delay before the
   *  payment flips to paid — we reload shortly after a success return. */
  private handleReturn() {
    const params = new URLSearchParams(location.search);
    if (params.get('paid')) {
      showToast(t('portal.toast.paymentReceived'), 'success');
      setTimeout(() => this.load(), 2500);
    } else if (params.get('canceled')) {
      showToast(t('portal.toast.paymentCanceled'), 'info');
    }
    if (params.has('paid') || params.has('canceled') || params.has('session_id')) {
      history.replaceState(null, '', location.pathname);
    }
  }

  private async load() {
    this.loading = true;
    try {
      const res = await api.get<{ payments: PortalPayment[]; credit_balance: number; online_pay_enabled?: boolean }>('/portal/payments');
      this.payments = res.payments; this.creditBalance = res.credit_balance ?? 0;
      this.onlinePay = !!res.online_pay_enabled;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async pay(p: PortalPayment) {
    this.payingId = p.id;
    try {
      const { url } = await api.post<{ url: string }>(`/portal/payments/${p.id}/pay`, {});
      window.location.href = url; // redirect to Stripe-hosted Checkout
    } catch (e) {
      showToast((e as Error).message, 'error');
      this.payingId = null;
    }
  }

  private money(p: PortalPayment) { return `${p.currency} ${Number(p.amount).toFixed(2)}`; }
  private fmt(d: string | null) { return d ? new Date(d.includes('T') ? d + 'Z' : d).toLocaleDateString() : '—'; }
  /** A human label for what the payment is for. Recurring fees carry a fee name; one-off
   *  charges (e.g. event fees) carry only a notes description — fall back to that. */
  private label(p: PortalPayment) { return p.fee_name || p.notes || t('portal.payments.paymentLabel'); }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    const outstanding = this.payments.filter((p) => p.status === 'pending' || p.status === 'overdue');
    const history = this.payments.filter((p) => p.status === 'paid' || p.status === 'cancelled');
    const dueTotal = outstanding.reduce((s, p) => s + Number(p.amount), 0);
    const currency = this.payments[0]?.currency ?? '';

    return html`
      <h1>${t('common.portalNav.payments')}</h1>

      <div class="summary">
        <div class="stat"><div class="k">${t('portal.payments.outstanding')}</div><div class="v ${dueTotal ? 'due' : ''}">${currency} ${dueTotal.toFixed(2)}</div></div>
        <div class="stat"><div class="k">${t('portal.payments.itemsDue')}</div><div class="v">${outstanding.length}</div></div>
        ${this.creditBalance > 0 ? html`<div class="stat"><div class="k">${t('portal.payments.creditBalance')}</div><div class="v" style="color:var(--color-success)">${currency} ${this.creditBalance.toFixed(2)}</div></div>` : ''}
      </div>
      ${this.creditBalance > 0 ? html`<p class="muted" style="margin:-8px 0 var(--space-4)">${t('portal.payments.creditNote')}</p>` : ''}

      <h2>${t('portal.payments.outstanding')}</h2>
      ${outstanding.length
        ? html`<div class="list">${outstanding.map((p) => html`
            <div class="item">
              <div>
                <div class="name">${this.label(p)}</div>
                <div class="meta">${t('portal.payments.dueLabel', { date: this.fmt(p.due_date) })}${p.period ? ' · ' + p.period : ''}${p.status === 'overdue' ? ' · ' + t('portal.payments.overdue') : ''}</div>
              </div>
              <div class="pay">
                <span class="amount">${this.money(p)}</span>
                ${this.onlinePay ? html`<cadence-button size="sm" ?loading=${this.payingId === p.id} ?disabled=${this.payingId !== null} @click=${() => this.pay(p)}>${t('portal.payments.pay')}</cadence-button>` : ''}
              </div>
            </div>`)}</div>
          <p class="muted" style="margin-top:10px">${this.onlinePay
            ? t('portal.payments.payNoteOnline')
            : t('portal.payments.payNoteOffline')}</p>`
        : html`<cadence-empty-state heading=${t('portal.payments.allPaidUp')} message=${t('portal.payments.noOutstanding')}></cadence-empty-state>`}

      <h2>${t('portal.payments.history')}</h2>
      ${history.length
        ? html`<div class="list">${history.map((p) => html`
            <div class="item">
              <div>
                <div class="name">${this.label(p)}</div>
                <div class="meta">${p.status === 'paid' ? t('portal.payments.paidOn', { date: this.fmt(p.paid_at) }) : t('portal.payments.cancelledStatus')}${p.method_name ? ` · ${p.method_name}` : ''}</div>
              </div>
              <span class="amount">${this.money(p)}</span>
            </div>`)}</div>`
        : html`<p class="muted">${t('portal.payments.noHistory')}</p>`}
    `;
  }
}
